import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithProvider } from "@/redux/baseQuery";

/** A resolved `ff-*` flag. `value` is whatever the cascade produced — flags are
 *  typed loosely on purpose, because not every one of them is a boolean. */
export interface FeatureFlag {
  slug: string;
  value: string | number | boolean | null;
  configurable: boolean;
  /** False when nothing in the cascade set a value; read it as "unset". */
  isValid: boolean;
  name?: string | null;
  description?: string | null;
}

export interface FeatureFlagsResponse {
  providerId: string;
  total: number;
  featureFlags: FeatureFlag[];
}

/**
 * The operator's own `ff-*` feature flags (web-app-v2 systemModule).
 *
 * One query for ALL of them rather than one per flag: they come from a single
 * subscription resolution on the server, so asking per flag would be the same
 * work repeated, and RTK Query caches the lot under one key. Read a flag with
 * `useFeatureFlag(slug)` rather than calling this directly.
 *
 * Until this existed the only way to reach a flag from the browser was a
 * bespoke endpoint per flag — `veivo-config` was one — because v2's general
 * endpoint is System-actor only. That is what this replaces.
 */
export const featureFlagApi = createApi({
  reducerPath: "featureFlagApi",
  baseQuery: baseQueryWithProvider(),
  tagTypes: ["FeatureFlag"],
  endpoints: (builder) => ({
    getFeatureFlags: builder.query<FeatureFlagsResponse, void>({
      query: () => "feature-flags",
      providesTags: ["FeatureFlag"],
    }),
  }),
});

export const { useGetFeatureFlagsQuery } = featureFlagApi;
